import { Routes } from '@/config/routes';
import { AUTH_TOKEN_KEY } from '@/lib/constants';
import type { SearchParamOptions } from '@/types';
import axios from 'axios';
import Cookies from 'js-cookie';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import Router from 'next/router';

const headers: Record<string, string> = {
  'Content-Type': 'application/json',
};

if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
  headers['X-From-Nextjs-Build'] = 'true';
}

const Axios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT,
  timeout: 5000000,
  headers: headers,
});

Axios.defaults.withCredentials = true;

Axios.interceptors.request.use((config) => {
  const token = Cookies.get(AUTH_TOKEN_KEY);

  const currentPageUrl =
    typeof window !== 'undefined' ? window.location.href : '';

  //@ts-ignore
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${token ? token : ''}`,
    'X-URL': currentPageUrl,
  };
  return config;
});

Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (
        error.response.status === 401 ||
        error.response.data.message === 'NOT_AUTHORIZED'
      ) {
        Cookies.remove(AUTH_TOKEN_KEY);
        Router.replace(Routes.home);
      } else if (error.response.status === 429) {
        const message =
          error.response.data?.message ||
          'Too many requests. Please wait and try again.';
        const retryAfter = error.response.data?.retry_after || 10;

        if (
          typeof window !== 'undefined' &&
          (window as any).showRateLimitModal
        ) {
          (window as any).showRateLimitModal(message, retryAfter);
        }
      }
    }
    return Promise.reject(error);
  },
);

export class HttpClient {
  static async get<T>(url: string, params?: unknown) {
    const response = await Axios.get<T>(url, { params });
    return response.data;
  }

  static async post<T>(url: string, data: unknown, options?: any) {
    const response = await Axios.post<T>(url, data, options);
    return response.data;
  }

  static async put<T>(url: string, data: unknown) {
    const response = await Axios.put<T>(url, data);
    return response.data;
  }

  static async delete<T>(url: string) {
    const response = await Axios.delete<T>(url);
    return response.data;
  }

  static formatSearchParams(params: Partial<SearchParamOptions>) {
    return Object.entries(params)
      .filter(([, value]) => Boolean(value))
      .map(([k, v]) =>
        ['categories', 'tags', 'author', 'manufacturer', 'shops'].includes(k)
          ? `${k}.slug:${v}`
          : `${k}:${v}`,
      )
      .join(';');
  }
}
